import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useFormatShortDate } from 'cockpit/services/format/useFormatShortDate';
import { pickInitialBookingStatus } from 'v5/api/booking/pickInitialBookingStatusFromV1.http';
import { formatResourcingCalendarDateRange } from 'v5/features/productions/resourcing/model/formatResourcingEventDateRange';
import {
  type BuildAssignmentCandidateContext,
  parseDate
} from './assignResourceCandidate';

/**
 * What a picked resource needs for the review and the optimistic booking: the production's dates
 * (Redux `productions`), its booking label, and the status a new booking starts in (`statuses`).
 */
export function useAssignmentCandidateContext(productionId: number) {
  const formatShortDate = useFormatShortDate();
  const productionDate = useSelector(
    (state: {
      productions?: {
        data?: Record<
          number,
          {
            production_date?: {
              start_date?: string;
              end_date?: string;
              all_day_duration_hours?: number | null;
            };
          }
        >;
      };
    }) => state?.productions?.data?.[productionId]?.production_date
  );
  const confirmedStatus = useSelector(
    (state: {
      statuses?: {
        data?: Record<
          string,
          {
            id?: number;
            name?: string;
            colour?: string;
            status_type?: string;
            entity_type?: string | null;
            order?: number | null;
          }
        >;
      };
    }) => {
      const statuses = Object.values(state?.statuses?.data ?? {}) as Array<{
        id?: number;
        name?: string;
        colour?: string;
        status_type?: string;
        entity_type?: string | null;
        order?: number | null;
      }>;
      return pickInitialBookingStatus(statuses);
    }
  );

  const productionRange = useMemo(() => {
    const start = parseDate(productionDate?.start_date);
    const end = parseDate(productionDate?.end_date);
    return start && end ? { start, end } : null;
  }, [productionDate?.end_date, productionDate?.start_date]);

  const bookingDateRangeLabel = useMemo(() => {
    if (!productionDate?.start_date || !productionDate?.end_date) {
      return null;
    }
    return (
      formatResourcingCalendarDateRange(
        {
          start: productionDate.start_date,
          end: productionDate.end_date
        },
        formatShortDate
      ) || null
    );
  }, [formatShortDate, productionDate?.end_date, productionDate?.start_date]);

  const assignmentCandidateContext = useMemo(
    (): BuildAssignmentCandidateContext => ({
      productionDate: productionDate ?? null,
      productionRange,
      bookingDateRangeLabel,
      confirmedStatus
    }),
    [productionDate, productionRange, bookingDateRangeLabel, confirmedStatus]
  );

  return { assignmentCandidateContext, bookingDateRangeLabel };
}
